import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const ImageComparison = ({ beforeImage, afterImage }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  /**
   * PIXEL-PERFECT NUDGE CONTROLS
   * If the source images have a slight offset in the screenshots, 
   * adjust these values (in pixels) to align them perfectly.
   */
  const NUDGE_X = 0; // Horizontal adjustment for the 'Before' image
  const NUDGE_Y = 0; // Vertical adjustment for the 'Before' image

  // Smooth hint animation on load
  useEffect(() => {
    const timer = setTimeout(() => {
      setSliderPosition(48);
      setTimeout(() => setSliderPosition(52), 400);
      setTimeout(() => setSliderPosition(50), 800);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = (x / rect.width) * 100;
    setSliderPosition(percent);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    const handleMouseMove = (e) => isDragging && handleMove(e.clientX);
    const handleTouchMove = (e) => isDragging && handleMove(e.touches[0].clientX);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="w-full max-w-[1100px] mx-auto group select-none">
      <div 
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] cursor-ew-resize touch-none bg-[#070b14]"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* BASE LAYER: AFTER IMAGE
            This image defines the natural height of the container. 
            Both images must be rendered at this EXACT size. */}
        <img 
          src={afterImage} 
          alt="After SEO Rankings" 
          className="w-full h-auto block pointer-events-none"
          draggable="false"
        />

        {/* OVERLAY LAYER: BEFORE IMAGE
            Positioned absolutely to match the base image 1:1.
            The clip-path ensures only the left portion is visible based on sliderPosition. */}
        <div 
          className="absolute inset-0 z-10 pointer-events-none"
          style={{ 
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
            transition: isDragging ? 'none' : 'clip-path 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <img 
            src={beforeImage} 
            alt="Before SEO Rankings" 
            className="w-full h-full pointer-events-none"
            style={{ 
              // Using transform for the nudge to avoid layout shifts
              transform: `translate(${NUDGE_X}px, ${NUDGE_Y}px)`,
              // Ensure it fills the exact same space as the base image
              objectFit: 'fill' 
            }}
            draggable="false"
          />
        </div>

        {/* INTERACTIVE SLIDER DIVIDER */}
        <div 
          className="absolute top-0 bottom-0 z-20 pointer-events-none flex items-center justify-center"
          style={{ 
            left: `${sliderPosition}%`,
            width: '2px',
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.4)',
            transform: 'translateX(-50%)',
            transition: isDragging ? 'none' : 'left 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          {/* HANDLE */}
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 text-slate-900 ring-[6px] ring-black/30">
            <SafeIcon icon={FiIcons.FiChevronLeft} className="text-lg -mr-1" />
            <SafeIcon icon={FiIcons.FiChevronRight} className="text-lg -ml-1" />
          </div>
        </div>

        {/* LABELS */}
        <div className="absolute top-4 left-4 md:top-8 md:left-8 z-30 pointer-events-none">
          <div className="px-3 py-1.5 md:px-5 md:py-2.5 rounded-xl bg-black/80 backdrop-blur-xl border border-white/10 text-[9px] md:text-[11px] font-black text-white uppercase tracking-[0.25em] shadow-2xl transition-opacity group-hover:opacity-40">
            Current State
          </div>
        </div>
        
        <div className="absolute top-4 right-4 md:top-8 md:right-8 z-30 pointer-events-none">
          <div className="px-3 py-1.5 md:px-5 md:py-2.5 rounded-xl bg-blue-600/90 backdrop-blur-xl border border-blue-400/30 text-[9px] md:text-[11px] font-black text-white uppercase tracking-[0.25em] shadow-2xl transition-opacity group-hover:opacity-40">
            PRBoost Impact
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageComparison;